interface RuntimeConfig {
  SITE_ID?: string;
  API_BASE_URL?: string;
  TENANT_PORTAL_DOMAIN?: string;
  SIDECAR_BASE_URL: string;
  LOGO_BASE64?: string;
  SITE_NAME?: string;
}

declare global {
  interface Window {
    __CONFIG__?: RuntimeConfig;
  }
}

function deriveApiDomain(): string {
  // Derive from hostname — works for all environments with one build:
  // member.nodus.nu → api.nodus.nu (dev)
  // member.nodus.foo → api.nodus.foo (staging)
  // member.nodus.se → api.nodus.se (prod)
  // customer-member.nodus.nu → api.nodus.nu (custom subdomain)
  // Also supports legacy tenant.* URLs during migration
  const host = window.location.hostname;
  if (host.includes("nodus.")) {
    const apiHost = host.replace(/^(member|tenant)\./, "api.");
    if (apiHost !== host) return apiHost;
    // Custom subdomains: extract nodus.<tld> and prepend api.
    const match = host.match(/nodus\.(\w+)$/);
    if (match) return `api.nodus.${match[1]}`;
  }
  if (import.meta.env.VITE_BASE_URL_DOMAIN) {
    return import.meta.env.VITE_BASE_URL_DOMAIN;
  }
  // Custom domains (member.l26.se etc.) → fall back to production API
  return "api.nodus.se";
}

function buildApiBaseUrl(): string {
  const domain = deriveApiDomain();
  const protocol = import.meta.env.VITE_BASE_URL_API_PROTOCOL || "https";
  const basePath = import.meta.env.VITE_BASE_URL_API_PATH || "/api";
  return `${protocol}://${domain}${basePath}`;
}

function buildSidecarBaseUrl(): string {
  const domain = deriveApiDomain();
  const protocol = import.meta.env.VITE_BASE_URL_API_PROTOCOL || "https";
  return `${protocol}://tenant-${domain}`;
}

let _config: RuntimeConfig | null = null;

export function getConfig(): RuntimeConfig {
  if (_config) return _config;

  const runtime = window.__CONFIG__;
  _config = {
    SITE_ID: runtime?.SITE_ID || import.meta.env.VITE_SITE_ID || undefined,
    API_BASE_URL: runtime?.API_BASE_URL || buildApiBaseUrl(),
    TENANT_PORTAL_DOMAIN:
      runtime?.TENANT_PORTAL_DOMAIN || deriveApiDomain(),
    SIDECAR_BASE_URL: buildSidecarBaseUrl(),
  };
  return _config;
}
